import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Close as CloseIcon,
  InsertDriveFile as FileIcon,
  Folder as FolderIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import fileService from '../../services/fileService';
import folderService from '../../services/folderService';
import { Folder } from '../../types';
import { formatFileSize } from '../../utils/formatters';

export interface PropertiesItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
}

interface ItemPropertiesDialogProps {
  open: boolean;
  onClose: () => void;
  item: PropertiesItem | null;
}

interface PropertyRow {
  label: string;
  value: string;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'MMM d, yyyy h:mm a');
};

const formatLocation = (path: unknown, parentName?: string): string => {
  if (typeof path === 'string' && path.trim()) return path;
  if (Array.isArray(path) && path.length > 0) {
    return path.map((segment) => (typeof segment === 'string' ? segment : segment?.name)).filter(Boolean).join(' / ');
  }
  if (parentName) return parentName;
  return '—';
};

const PropertyList: React.FC<{ rows: PropertyRow[] }> = ({ rows }) => (
  <List dense disablePadding>
    {rows.map((row) => (
      <ListItem key={row.label} disableGutters sx={{ py: 0.75 }}>
        <ListItemText
          primary={row.label}
          secondary={row.value}
          primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
          secondaryTypographyProps={{
            variant: 'body2',
            color: 'text.primary',
            sx: { wordBreak: 'break-word' },
          }}
        />
      </ListItem>
    ))}
  </List>
);

const ItemPropertiesDialog: React.FC<ItemPropertiesDialogProps> = ({ open, onClose, item }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<PropertyRow[]>([]);

  useEffect(() => {
    if (!open || !item) {
      setRows([]);
      setError('');
      return;
    }

    let cancelled = false;

    const loadProperties = async () => {
      setLoading(true);
      setError('');
      setRows([]);

      try {
        if (item.type === 'file') {
          const file = await fileService.getFile(item.id);
          const ownerName = file.owner?.name || (file as { ownerName?: string }).ownerName;
          const mimeType = file.mimetype || (file as { mimeType?: string }).mimeType;
          const formattedSize =
            (file as { formattedSize?: string }).formattedSize ||
            (typeof file.size === 'number' ? formatFileSize(file.size) : '—');

          if (!cancelled) {
            setRows([
              { label: 'Name', value: file.name || item.name },
              { label: 'Type', value: 'File' },
              { label: 'Size', value: formattedSize },
              ...(mimeType ? [{ label: 'MIME type', value: mimeType }] : []),
              ...((file as { extension?: string }).extension
                ? [{ label: 'Extension', value: `.${(file as { extension?: string }).extension}` }]
                : []),
              {
                label: 'Location',
                value: formatLocation(file.path, (file as { parent?: { name?: string } }).parent?.name),
              },
              { label: 'Owner', value: ownerName || '—' },
              { label: 'Created', value: formatDateTime(file.createdAt) },
              { label: 'Modified', value: formatDateTime(file.updatedAt) },
              ...((file as { lastAccessedAt?: string }).lastAccessedAt
                ? [{ label: 'Last accessed', value: formatDateTime((file as { lastAccessedAt?: string }).lastAccessedAt) }]
                : []),
              {
                label: 'Favorites',
                value: (file as { isStarred?: boolean }).isStarred ? 'Yes' : 'No',
              },
              ...((file as { version?: number }).version != null
                ? [{ label: 'Version', value: String((file as { version?: number }).version) }]
                : []),
            ]);
          }
        } else {
          const { folder, subfolders, files } = await folderService.getFolderDetails(item.id);
          const folderData = folder as Folder & {
            owner?: { name?: string };
            parent?: { name?: string };
          };
          const subfolderCount = subfolders.length;
          const fileCount = files.length;

          if (!cancelled) {
            setRows([
              { label: 'Name', value: folderData.name || item.name },
              { label: 'Type', value: 'Folder' },
              {
                label: 'Location',
                value: formatLocation(folderData.path, folderData.parent?.name),
              },
              {
                label: 'Contains',
                value: `${subfolderCount} folder${subfolderCount === 1 ? '' : 's'}, ${fileCount} file${fileCount === 1 ? '' : 's'}`,
              },
              { label: 'Owner', value: folderData.owner?.name || folderData.ownerName || '—' },
              { label: 'Created', value: formatDateTime(folderData.createdAt) },
              { label: 'Modified', value: formatDateTime(folderData.updatedAt) },
              { label: 'Favorites', value: folderData.isStarred ? 'Yes' : 'No' },
              ...(folderData.isShared != null
                ? [{ label: 'Shared', value: folderData.isShared ? 'Yes' : 'No' }]
                : []),
            ]);
          }
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const message =
            (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
            (err instanceof Error ? err.message : 'Failed to load properties');
          setError(message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadProperties();

    return () => {
      cancelled = true;
    };
  }, [open, item]);

  if (!item) return null;

  const isFile = item.type === 'file';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            {isFile ? (
              <FileIcon color="primary" />
            ) : (
              <FolderIcon color="primary" />
            )}
            <Typography variant="h6" noWrap>
              {isFile ? 'File' : 'Folder'} properties
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'text.secondary' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1, wordBreak: 'break-word' }}>
          {item.name}
        </Typography>

        <Divider sx={{ mb: 1 }} />

        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && !loading && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && rows.length > 0 && <PropertyList rows={rows} />}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemPropertiesDialog;
